import axios from 'axios';
import { UUID } from 'crypto';
import { requireApp } from 'store/assertions';

const getApiClient = () => requireApp().apiClient;
const getProjectId = () => requireApp().projectId;
const getUploadClient = () =>
    axios.create({
        baseURL: '/api/',
        withCredentials: true,
    });

type ObservationRequest = {
    title: string;
    description: string;
    grid_id: UUID;
    reportId?: UUID | null;
    reportName?: string | null;
};

type ObservationResponse = {
    id: UUID;
    reportId: UUID;
};

export type ReportResponse = {
    id: UUID;
    project_id: UUID;
    name: string;
    deleted_at: Date | null;
};

async function createReport(name: string): Promise<ReportResponse> {
    const response = await getApiClient().post(`projects/${getProjectId()}/reports`, { name });
    return response.data;
}

async function resolveReportId(obsInfo: ObservationRequest): Promise<UUID> {
    if (obsInfo.reportId) {
        return obsInfo.reportId;
    }

    const reportName = obsInfo.reportName?.trim();
    if (!reportName) {
        throw new Error('Observation requires a report ID or report name');
    }

    const report = await createReport(reportName);
    return report.id;
}

async function postObservation(obsInfo: ObservationRequest, screenshotDataBlob: Blob, stateBlob: Blob): Promise<ObservationResponse> {
    const reportId = await resolveReportId(obsInfo);
    const parsedState = JSON.parse(await stateBlob.text()) as { version?: number; state?: unknown };
    const imageFile = new File([screenshotDataBlob], 'observation.png', { type: screenshotDataBlob.type || 'image/png' });
    const formData = new FormData();

    formData.append('gridId', obsInfo.grid_id);
    formData.append('title', obsInfo.title);

    if (obsInfo.description) {
        formData.append('description', obsInfo.description);
    }

    formData.append('image', imageFile);
    formData.append(
        'state',
        JSON.stringify({
            version: parsedState.version ?? 0,
            state: parsedState.state ?? parsedState,
        })
    );

    const response = await getUploadClient().post(`projects/${getProjectId()}/reports/${reportId}/observations`, formData);
    return response.data;
}

/**
 * Creates an observation
 *
 * @param obsInfo Observation data
 * @param screenshotDataBlob blob of the screenshot of the observation
 * @param stateBlob blob of the state to store
 */
export async function createObservation(obsInfo: ObservationRequest, screenshotDataBlob: Blob, stateBlob: Blob): Promise<void> {
    await postObservation(obsInfo, screenshotDataBlob, stateBlob);
}

export async function getReports(): Promise<ReportResponse[]> {
    const response = await getApiClient().get(`projects/${getProjectId()}/reports`);
    return response.data;
}

export async function renameReport(reportId: string, newName: string): Promise<void> {
    await getApiClient().patch(`projects/${getProjectId()}/reports/${reportId}`, { name: newName });
    return;
}
